// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@carbon/react';
import { Checkmark, Link as LinkIcon } from '@carbon/icons-react';
import type { WorkOrderStatus } from '../../types/workOrder';

export type ChipTone = 'fault' | 'warn' | 'helios' | 'ok' | 'idle';

/** Chip tone for a WOMS status. */
export function workOrderTone(status: WorkOrderStatus | null | undefined): ChipTone {
  switch (status) {
    case 'in_progress':
      return 'helios';
    case 'completed':
      return 'ok';
    case 'on_hold':
      return 'warn';
    case 'canceled':
      return 'fault';
    default:
      return 'idle';
  }
}

/** A work order linked to the event, resolved by the caller (alarm or anomaly). */
export interface LinkedWorkOrder {
  /** Human reference, e.g. "WO-10482". */
  reference: string;
  /** Route that opens the order in Work orders. */
  to: string;
  /** The order's own status in the WOMS, once read. */
  statusLabel?: string | null;
  statusTone?: ChipTone;
  /** Start work on the order; absent with `startBlocked` says why it cannot start. */
  onStart?: () => void;
  startBlocked?: string;
  /** Mark the order complete; absent with `completeBlocked` says why not. */
  onComplete?: () => void;
  completeBlocked?: string;
  /** Which WOMS write is in flight for this order. */
  pending?: 'start' | 'complete' | null;
}

export interface WorkOrdersPanelProps {
  workOrders: LinkedWorkOrder[];
  /** Offer to raise one while the list is empty. */
  onCreate?: () => void;
  /** Why raising one is blocked right now: the prompt then shows disabled with this reason. */
  createBlocked?: string;
  /** A failed WOMS write, shown under the list. */
  error?: string | null;
  className?: string;
}

/**
 * Title-less card listing the work orders raised for an event, each as a chip
 * with its WOMS status beside it and the actions the order takes. Knows nothing
 * about alarms or anomalies: the caller resolves the links, statuses and
 * writes (alarms via useRaisedWorkOrder and useWorkOrderStatus).
 */
export default function WorkOrdersPanel({
  workOrders,
  onCreate,
  createBlocked,
  error,
  className,
}: WorkOrdersPanelProps) {
  return (
    <section className={className} aria-label="Work orders" data-testid="work-orders-panel">
      {workOrders.length === 0 ? (
        <div className="wop-row">
          <p className="wop-empty">No work orders yet</p>
          {(onCreate || createBlocked) && (
            <Button
              kind="ghost"
              size="sm"
              disabled={!onCreate}
              title={createBlocked}
              onClick={onCreate}
            >
              New work order
            </Button>
          )}
        </div>
      ) : (
        <ul className="wop-list">
          {workOrders.map(order => (
            <li key={order.to} className="wop-row" data-testid="work-order-row">
              <span className="wop-ref">
                <Link className="ald-wo-chip" to={order.to}>
                  {order.reference}
                  <LinkIcon size={12} aria-hidden="true" />
                </Link>
                {(order.onComplete || order.completeBlocked) && (
                  <Button
                    hasIconOnly
                    kind="ghost"
                    size="sm"
                    renderIcon={Checkmark}
                    iconDescription={
                      order.pending === 'complete'
                        ? 'Marking complete…'
                        : 'Mark work order complete'
                    }
                    tooltipPosition="bottom"
                    disabled={!order.onComplete || !!order.pending}
                    title={order.completeBlocked}
                    onClick={order.onComplete}
                  />
                )}
              </span>
              <span className="wop-side">
                {(order.onStart || order.startBlocked) && (
                  <Button
                    kind="ghost"
                    size="sm"
                    disabled={!order.onStart || !!order.pending}
                    title={order.startBlocked}
                    onClick={order.onStart}
                  >
                    {order.pending === 'start' ? 'Starting…' : 'Start work'}
                  </Button>
                )}
                {order.statusLabel && (
                  <span
                    className={`ald-ns-state ald-ns-state--${order.statusTone ?? 'idle'}`}
                    data-testid="work-order-status"
                  >
                    {order.statusLabel}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="ald-wo-cmms-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
